import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Card, CardBody, CardHeader, Col, Container, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, PaginationItem, PaginationLink, Popover, PopoverBody, Row, Table } from "reactstrap";
import ItemsBloc from "./ItemsBloc";
import { set } from "lodash";
import Swal from "sweetalert2";
import { purchaseActions } from "../../sagas/purchaseSlice";
import { supplierActions } from "../../sagas/supplierSlice";
import { FaAngleDoubleRight, FaEdit, FaEllipsisH, FaFilePdf, FaList, FaPlus, FaTruck, FaUser } from "react-icons/fa";
import Select from 'react-select/async';
import { components } from 'react-select';
import { Spinner } from 'reactstrap';
import ReceiptNoteForm from "../Reception/ReceiptNoteForm";
import { receiptNoteActions } from "../../sagas/receiptNoteSlice";
import { formatPrice } from "../../helpers/utility";

const initForm = {
    numero: '',
    fournisseurId: null,
    fournisseurNom: '',
    dateCommande: new Date().toISOString().slice(0, 10),
    updatedAt: null,
    lignes: [],
    statut: 'EN_ATTENTE',
    montantTotalHT: 0,
    montantTotalTaxe: 0,
    montantTotalTTC: 0,
    modeReglement: 'ESPECE',
    addresseLivraison: ''
};

const PurchaseForm = ({ onClose, purchase }) => {
    const { t } = useTranslation();
    const [formState, setForm] = useState(purchase || initForm);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { company } = useSelector(statut => statut.auth);
    const [showSupplierModal, setShowSupplierModal] = useState(false);
    const [newSupplier, setNewSupplier] = useState({
        id: null,
        entreprise: '',
        ice: '',
        ife: '',
        rc: '',
        adresse: '',
        telephone1: '',
        telephone2: '',
        email: ''
    });
    const { newOrderNumber, currentPurchase, isLoading } = useSelector(statut => statut.purchases);
    const [showReceiptNoteModal, setShowReceiptNoteModal] = useState(false);
    const [receiptNoteForm, setReceiptNoteForm] = useState(null);
    const { receiptNotes, newBRNumber, isLoading: receiptNoteLoading } = useSelector(statut => statut.receiptNote);
    const [receiptNoteCount, setReceiptNoteCount] = useState(0);
    const [showReceiptNoteListModal, setShowReceiptNoteListModal] = useState(false);

    // Add this statut to store cached options
    const [cachedOptions, setCachedOptions] = useState([]);
    console.log(purchase)
    // Modify the loadSupplierOptions function
    const loadSupplierOptions = async (inputValue) => {
        return new Promise((resolve) => {
            dispatch(supplierActions.search({
                query: inputValue,
                onSuccess: (data) => {
                    const options = data.content.map(supplier => ({
                        value: supplier.id,
                        label: (supplier.code ? supplier.code + ' - ' : '') + supplier.entreprise,
                        supplier: supplier
                    }));
                    setCachedOptions(options); // Cache the options
                    resolve(options);
                }
            }));
        });
    };

    // Add a function to force reload options
    const forceReloadOptions = () => {
        dispatch(supplierActions.search({
            query: '',
            onSuccess: (data) => {
                const options = data.content.map(supplier => ({
                    value: supplier.id,
                    label: (supplier.code ? supplier.code + ' - ' : '') + supplier.entreprise,
                    supplier: supplier
                }));
                setCachedOptions(options);
            }
        }));
    };



    useEffect(() => {
        loadSupplierOptions(''); // Load all suppliers on component mount
        if (purchase) {
            fetchReceiptNotesByPurchaseId(purchase.id);
        }
    }, [purchase, dispatch]);

    useEffect(() => {
        if (purchase && receiptNotes) {
            setReceiptNoteCount(receiptNotes.length);
        }
    }, [receiptNotes]);



    const fetchReceiptNotesByPurchaseId = (id) => {
        dispatch(receiptNoteActions.getReceiptNotesByPurchaseId({ id }));
    }

    const handleShowReceiptNoteList = () => {
        setShowReceiptNoteListModal(true);
    };

    const handleCloseReceiptNoteListModal = () => {
        setShowReceiptNoteListModal(false);
    };

    const formik = useFormik({
        initialValues: { ...formState, numero: purchase ? formState.numero : newOrderNumber, addresseLivraison: purchase ? formState.addresseLivraison : company.address },
        enableReinitialize: true,
        onSubmit: (values) => {
            Swal.fire({
                title: t('purchase.confirmation'),
                text: t('purchase.saveOrConfirmText'),
                icon: 'question',
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: t('common.saveAndConfirm'),
                denyButtonText: t('common.save'),
                cancelButtonText: t('common.cancel'),

            }).then((result) => {
                if (result.isConfirmed) {
                    values.statut = 'VALIDE';
                    values.updatedAt = null;
                    values.createdAt = null;

                    const payload = {
                        data: values,
                        onSuccess: () => {
                            Swal.fire({
                                icon: 'success',
                                title: t('common.saved'),
                                text: t('purchase.savedSuccess'),
                            });
                            onClose();
                        }
                    };

                    if (purchase) {
                        dispatch(purchaseActions.updatePurchase({...payload, id: purchase.id}));
                    } else {
                        dispatch(purchaseActions.createPurchase(payload));
                    }
                } else if (result.isDenied) {
                    values.statut = 'EN_ATTENTE';
                    values.updatedAt = null;
                    values.createdAt = null;
                    const payload = {
                        data: values,
                        onSuccess: () => {
                            Swal.fire({
                                icon: 'success',
                                title: t('common.saved'),
                                text: t('purchase.savedSuccess'),
                            });
                            onClose();
                        }
                    };

                    if (purchase) {
                        dispatch(purchaseActions.updatePurchase({...payload, id: purchase.id}));
                    } else {
                        dispatch(purchaseActions.createPurchase(payload));
                    }
                } else {

                }
            });
        }
    });

    // Add this function to handle opening the receipt note modal
    const handleCreateReception = () => {
        // Populate receipt note form with data from purchase order
        const newReceiptNote = {
            commandeId: values.id,
            fournisseurId: values.fournisseurId,
            fournisseurNom: values.fournisseurNom,
            dateReception: new Date().toISOString().slice(0, 10),
            lignes: values.lignes.map(line => ({
                articleId: line.articleId,
                articleNom: line.articleNom,
                quantiteCommandee: line.quantiteCommandee,
                quantiteRecue: line.quantiteCommandee,
                prixUnitaireHT: line.prixUnitaireHT,
                montantHT: line.montantHT,
                tauxTVA: line.tauxTVA,
                montantTTC: line.montantTTC
            })),
            montantTotalHT: values.montantTotalHT,
            montantTotalTTC: values.montantTotalTTC,
            montantTotalTaxe: values.montantTotalTaxe,
            statut: 'EN_ATTENTE',
        };

        dispatch(receiptNoteActions.fetchNewBRNumber());
        setReceiptNoteForm(newReceiptNote);
        setShowReceiptNoteModal(true);
    };

    // Add this function to handle closing the receipt note modal
    const handleCloseReceiptNoteModal = () => {
        setShowReceiptNoteModal(false);
        setReceiptNoteForm(null);
        fetchReceiptNotesByPurchaseId(values.id);
    };


    const handleSupplierChange = (selectedOption) => {
        formik.setFieldValue('lignes', []); // Clear the lignes when the supplier changes
        formik.setFieldValue('fournisseurId', selectedOption?.value || null);
        formik.setFieldValue('fournisseurNom', selectedOption?.label || '');
        formik.setFieldValue('montantTotalTTC', 0);
        formik.setFieldValue('montantTotalHT', 0);
        formik.setFieldValue('montantTotalTaxe', 0);

        // If selection is cleared (selectedOption is null), reload all options
        if (!selectedOption) {
            // Load all suppliers (empty query will return all)
            dispatch(supplierActions.search({
                query: '',
                onSuccess: (data) => {
                    const options = data.content.map(supplier => ({
                        value: supplier.id,
                        label: (supplier.code ? supplier.code + ' - ' : '') + supplier.entreprise,
                        supplier: supplier
                    }));
                    setCachedOptions(options); // Update cached options
                }
            }));
        }
    };



    const { values, getFieldProps, handleSubmit } = formik;


    const handleCreateSupplier = () => {
        setShowSupplierModal(true);
    };

    const handleGeneratePdf = (note) => {
        dispatch(receiptNoteActions.generateDocument({ id: note.id }));
    };


    const handleSupplierSubmit = () => {
        if (!newSupplier.entreprise) {
            Swal.fire({
                icon: 'error',
                title: t('common.error'),
                text: t('supplier.missingFields'),
            });
            return;
        }
        dispatch(supplierActions.addSupplier({
            data: newSupplier,
            onSuccess: (data) => {
                setShowSupplierModal(false);

                // Force reload the supplier options
                forceReloadOptions();

                // Create a new option for the select component
                const newOption = {
                    value: data.id,
                    label: (data.code ? data.code + ' - ' : '') + data.entreprise,
                    supplier: data
                };

                // Update formik values with the new supplier
                handleSupplierChange(newOption);

                // Reset the new supplier form
                setNewSupplier({
                    id: null,
                    entreprise: '',
                    ice: '',
                    ife: '',
                    rc: '',
                    adresse: '',
                    telephone1: '',
                    telephone2: '',
                    email: ''
                });

                Swal.fire({
                    icon: 'success',
                    title: t('common.saved'),
                    text: t('supplier.savedSuccess')
                });
            }
        }));
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'EN_ATTENTE':
                return <Badge color="secondary">En attente confirmation</Badge>;
            case 'VALIDE':
                return <Badge color="info">Commandé</Badge>;
            case 'LIVRE':
                return <Badge color="primary">Livré</Badge>;
            case 'ANNULE':
                return <Badge color="danger">Annulé</Badge>;
            default:
                return <Badge color="light">-</Badge>;
        }
    };

    const getReceptionStatusBadge = (status) => {
        switch (status) {
            case 'EN_ATTENTE':
                return <Badge color="secondary">En attente confirmation</Badge>;
            case 'VALIDE':
                return <Badge color="success">Confirmée</Badge>;
            
            default:
                return <Badge color="light">-</Badge>;
        }
    };
    console.log('values', values);
    return (
        <Container fluid>
            {(isLoading || receiptNoteLoading) && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                </div>
            )}
            <Form onSubmit={handleSubmit} onAbort={onClose}>
                <Card>
                    <CardHeader>
                        <Row className="align-items-center">
                            <Col md="9">
                                <Row>
                                    <Col md="3">
                                        <Label htmlFor="numero">{t('purchase.orderNumber')}:</Label>
                                        <Input
                                            type="text"
                                            id="numero"
                                            {...getFieldProps('numero')}
                                            disabled
                                            className="form-control"
                                        />
                                    </Col>
                                    <Col md="3">
                                        <Label htmlFor="orderDate">{t('purchase.orderDate')}:</Label>
                                        <Input
                                            type="date"
                                            id="orderDate"
                                            {...getFieldProps('dateCommande')}
                                            className="form-control"
                                            disabled={values.statut !== 'EN_ATTENTE'}
                                        />
                                    </Col>
                                    <Col md="4">
                                        <Label htmlFor="supplier">{t('purchase.supplier')}:</Label>
                                        <Select
                                            id="supplier"
                                            name="supplier"
                                            isClearable
                                            placeholder={t('purchase.selectSupplier')}
                                            loadOptions={loadSupplierOptions}
                                            onChange={handleSupplierChange}
                                            defaultOptions={cachedOptions} // Use cached options
                                            value={values.fournisseurId ? {
                                                value: values.fournisseurId,
                                                label: values.fournisseurNom
                                            } : null}
                                            className="react-select"
                                            classNamePrefix="select"
                                            noOptionsMessage={() => t('purchase.noSupplierFound')}
                                            loadingMessage={() => t('common.loading')}
                                            components={{
                                                DropdownIndicator: () => <FaUser className="me-2" />,
                                                Option: ({ children, ...props }) => (
                                                    <components.Option {...props}>{children}</components.Option>
                                                ),
                                                MenuList: ({ children, ...props }) => (
                                                    <components.MenuList {...props}>
                                                        {children}
                                                        <div className="border-top p-2">
                                                            <Button
                                                                color="primary"
                                                                size="sm"
                                                                block
                                                                onClick={handleCreateSupplier}
                                                            >
                                                                <FaPlus className="me-1" /> {t('supplier.new')}
                                                            </Button>
                                                        </div>
                                                    </components.MenuList>
                                                )
                                            }}
                                        />
                                        {formik.touched.fournisseurId && formik.errors.fournisseurId && (
                                            <div className="invalid-feedback d-block">
                                                {formik.errors.fournisseurId}
                                            </div>
                                        )}
                                    </Col>
                                    <Col md="2">
                                        <Label htmlFor="statut">{t('purchase.statut.title')}:</Label>
                                        <div className="form-control-plaintext">
                                            {getStatusBadge(values.statut)}
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                            <Col md="3" className="text-end">
                                {(values.statut === 'VALIDE' || values.statut === 'LIVRE') && (
                                    <>
                                        <Button color="success" onClick={handleCreateReception} className="me-2" size="sm">
                                            <FaTruck className="me-1" />
                                            {t('purchase.newReception')}
                                        </Button>
                                    </>
                                )}
                                {receiptNoteCount > 0 && (
                                    <Button color="info" onClick={handleShowReceiptNoteList} className="me-2" size="sm" aria-label={t('reception.receiptNotes')}>
                                        <FaList className="mx-2" /> {`${t('reception.receiptNotes')} (${receiptNoteCount})`}
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </CardHeader>
                    <CardBody>
                        <Row>
                            <ItemsBloc formik={formik} />
                        </Row>
                        <Row className="mt-3">
                            <Col md="8">
                                <Label htmlFor="addresseLivraison">{t('purchase.deliveryAddress')}:</Label>
                                <Input
                                    type="textarea"
                                    id="addresseLivraison"
                                    {...getFieldProps('addresseLivraison')}
                                    className="form-control"
                                    defaultValue={company.address} // Set default value to company address
                                />
                            </Col>
                            <Col md={4}>
                                <Label htmlFor="newPaymentMode">{t('facture.modeReglement')} :</Label>
                                <select
                                    id="modeReglement"
                                    className="form-control"
                                    value={formik.values.modeReglement || 'ESPECE'}
                                    onChange={(e) => formik.setFieldValue('modeReglement', e.target.value)}
                                >
                                    <option value="ESPECE">{t('facture.modeReglement.espece')}</option>
                                    <option value="CHEQUE">{t('facture.modeReglement.cheque')}</option>
                                    <option value="VIREMENT">{t('facture.modeReglement.virement')}</option>
                                    <option value="CARD">{t('facture.modeReglement.card')}</option>
                                </select>
                            </Col>
                        </Row>
                        <Row className="mt-3 justify-content-end">
                            <Col md={4}>
                                <div className="totals text-center">
                                    <p><strong>{t('cart.totalHT')}:</strong> {formatPrice(values.montantTotalHT ?? 0)}</p>
                                    <p><strong>{t('cart.totalTVA')}:</strong> {formatPrice(values.montantTotalTaxe ?? 0)}</p>
                                    <p><strong>{t('cart.totalTTC')}:</strong> {formatPrice(values.montantTotalTTC ?? 0)}</p>
                                </div>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col>
                                <Button type="submit" color="primary" className="me-2"
                                    disabled={values.statut !== 'EN_ATTENTE'}
                                >
                                    {t('common.save')}
                                </Button>
                                <Button type="button" color="secondary" onClick={onClose}>
                                    {t('common.cancel')}
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </Form>
            <Modal isOpen={showSupplierModal} toggle={() => setShowSupplierModal(false)} size="xl">
                <ModalHeader toggle={() => setShowSupplierModal(false)}>
                    {t('supplier.new')}
                </ModalHeader>
                <ModalBody>
                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label>{t('supplier.entreprise')}</Label>
                                <Input
                                    value={newSupplier.entreprise}
                                    onChange={e => setNewSupplier({ ...newSupplier, entreprise: e.target.value })}

                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="3">
                            <FormGroup>
                                <Label>{t('supplier.ice')}</Label>
                                <Input
                                    value={newSupplier.ice}
                                    onChange={e => setNewSupplier({ ...newSupplier, ice: e.target.value })}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>{t('supplier.ife')}</Label>
                                <Input
                                    value={newSupplier.ife}
                                    onChange={e => setNewSupplier({ ...newSupplier, ife: e.target.value })}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>{t('supplier.rc')}</Label>
                                <Input
                                    value={newSupplier.rc}
                                    onChange={e => setNewSupplier({ ...newSupplier, rc: e.target.value })}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>{t('supplier.telephone1')}</Label>
                                <Input
                                    value={newSupplier.telephone1}
                                    onChange={e => setNewSupplier({ ...newSupplier, telephone1: e.target.value })}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="3">
                            <FormGroup>
                                <Label>{t('supplier.telephone2')}</Label>
                                <Input
                                    value={newSupplier.telephone2}
                                    onChange={e => setNewSupplier({ ...newSupplier, telephone2: e.target.value })}
                                />
                            </FormGroup>
                        </Col>

                        <Col md="3">
                            <FormGroup>
                                <Label>{t('supplier.email')}</Label>
                                <Input
                                    type="email"
                                    value={newSupplier.email}
                                    onChange={e => setNewSupplier({ ...newSupplier, email: e.target.value })}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label>{t('supplier.address')}</Label>
                                <Input
                                    type="textarea"
                                    rows={3}
                                    value={newSupplier.adresse}
                                    onChange={e => setNewSupplier({ ...newSupplier, adresse: e.target.value })}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleSupplierSubmit}>
                        {t('common.save')}
                    </Button>
                    <Button color="secondary" onClick={() => setShowSupplierModal(false)}>
                        {t('common.cancel')}
                    </Button>
                </ModalFooter>
            </Modal>
            <Modal isOpen={showReceiptNoteModal} toggle={handleCloseReceiptNoteModal} size="xl">
                <ModalHeader toggle={handleCloseReceiptNoteModal}>
                    {t('reception.new')} - {receiptNoteForm?.numero || newBRNumber}
                </ModalHeader>
                <ModalBody>
                    {receiptNoteForm && (
                        <ReceiptNoteForm
                            initialValues={receiptNoteForm}
                            onClose={handleCloseReceiptNoteModal}
                            purchaseOrder={values}
                        />
                    )}
                </ModalBody>
            </Modal>
            <Modal isOpen={showReceiptNoteListModal} toggle={handleCloseReceiptNoteListModal} size="xl">
                <ModalHeader toggle={handleCloseReceiptNoteListModal}>
                    {t('reception.receiptNotes')}
                </ModalHeader>
                <ModalBody>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>{t('reception.receiptNumber')}</th>
                                <th>{t('reception.dateReception')}</th>
                                <th>{t('reception.deliveryNoteNb')}</th>
                                <th>{t('reception.status')}</th>
                                <th className="align-middle text-center">{t('common.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {receiptNotes && receiptNotes.map(note => (
                                <tr key={note.id}>
                                    <td>{note.numero}</td>
                                    <td>{new Date(note.dateReception).toLocaleDateString()}</td>
                                    <td>{note.deliveryNoteNb}</td>
                                    <td>{getReceptionStatusBadge(note.statut)}</td>
                                    <td className="align-middle text-center">
                                        <>
                                            {/* <Button color="secondary" title='Téléchargé PDF' onClick={() => handleGeneratePdf(note)} size="sm" className="me-1 px-2 py-1">
                                                <FaFilePdf size={12} className="mr-2" />
                                            </Button> */}

                                            <Button color="warning" title='Consulter' onClick={() => {
                                                setReceiptNoteForm(note);
                                                setShowReceiptNoteModal(true);
                                            }} size="sm" className="px-2 py-1">
                                                <FaEdit size={12} className="mr-2" />
                                            </Button>
                                        </>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={handleCloseReceiptNoteListModal}>
                        {t('common.cancel')}
                    </Button>
                </ModalFooter>
            </Modal>
        </Container>
    );
};

export default PurchaseForm;